import axios from "axios";
import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, reactive, ref } from "vue";
import useHelpers from "composables/useHelpers";
import { useRouter } from "vue-router";
import { shouldFetch } from "common/fetchHelpers.js";
import { useQuasar } from "quasar";
import { cloneDeep } from "lodash";
import { useOverviewStore } from "./overviewStore";
import { TransitionMeeting } from "models/meeting/transitionMeetingModel";
import { Meeting } from "models/meeting/meetingModel";
import { GroupMeeting } from "models/meeting/groupMeetingModel.js";

/**
 * Meeting Store
 *
 * This store manages the state and actions related to meetings.
 */
export const useMeetingStore = defineStore("meeting", () => {
  // Router
  const router = useRouter();
  const $q = useQuasar();

  // Refs
  const user = ref(JSON.parse(localStorage.getItem("user")));
  const meeting = ref({});
  const studentMeetings = ref([]);
  const initialMeeting = ref({});
  const initialStudents = ref([]);
  const initialSchedules = ref([]);
  const teachers = ref([]);
  const meetings = ref([]);
  const myStudentsGroupMeetings = ref([]);
  const myLessongroupsGroupMeetings = ref([]);
  const studentCheckinQuestions = ref([]);
  const timelines = ref({});
  const meetingTimelineTemplates = ref([]);
  const meetingTimelineOptions = ref([]);

  const hasFetched = ref({
    teachers: false,
    meetings: false,
    myStudentsGroupMeetings: false,
    myLessongroupsGroupMeetings: false,
    studentCheckinQuestions: false,
    studentMeetings: false,
    timelines: {},
    meetingTimelineTemplates: false,
  });

  const isFetching = reactive({
    studentCheckinQuestions: false,
    studentMeetings: false,
    teachers: false,
    meetingTimelineTemplates: false,
  });

  // Computed
  /**
   * Determines if the user is allowed to perform certain actions.
   * @returns {boolean}
   */
  const isAllowed = computed(() => {
    const allowedRoles = ["manager", "webmaster", "organizer"];
    return user.value.role.some((role) => allowedRoles.includes(role));
  });

  const hasFetchedTimeline = (meetingId) => {
    if (!hasFetched.value.timelines?.[meetingId]) {
      return false;
    }

    return hasFetched.value.timelines[meetingId];
  };

  const fetchMeetingTimeline = async ({
    meetingId,
    meetingType,
    myRatings,
    forceFetch = false,
  } = {}) => {
    const { schoolId } = useHelpers();

    if (!shouldFetch(hasFetchedTimeline(meetingId)) && !forceFetch) {
      return timelines.value[meetingId];
    }

    try {
      let url;

      switch (meetingType) {
        case "group_meeting":
          url = `api/school/${schoolId}/group-meetings/${meetingId}/timeline`;
          break;
        case "transition_meeting":
          url = `api/school/${schoolId}/transition_meeting/${meetingId}/global-timeline`;
          break;
        default:
          url = `api/meetings/${meetingId}/timeline`;
          break;
      }

      const { data } = await axios.get(url, {
        params: {
          ...(myRatings && { myratings: true }),
        },
      });

      hasFetched.value.timelines[meetingId] = new Date();

      timelines.value[meetingId] = data;

      return timelines.value[meetingId];
    } catch (error) {
      console.log("🚀 ~ fetchMeetingTimeline ~ error:", error);

      if (axios.isAxiosError(error)) {
        return Promise.reject(error);
      }
    }
  };

  /**
   * Determines if the current route is an overview.
   * @returns {boolean}
   */
  const isOverview = computed(() => {
    return !!router.currentRoute.value.query.overview;
  });

  /**
   * Determines if the current meeting is editable.
   * @returns {boolean}
   */
  const isEditable = computed(() => {
    const statusCanEdit = {
      meeting: ["concept", "open"],
      group_meeting: ["concept", "open"],
      transition_meeting: ["concept", "open", "closed_modify"],
    };

    return statusCanEdit[meeting.value.meeting_type].includes(
      meeting.value.status,
    );
  });

  /**
   * Configuration for group meetings.
   * @returns {Object}
   */
  const groupMeetingConfig = computed(() => {
    const ratingLabels = {
      1: "Excellent",
      2: "Ga zo door",
      3: "Let op",
      4: "Pas op",
      5: "Geen beoordeling",
    };

    const backgroundColors = {
      1: "#000",
      2: "#72a886",
      3: "#fcb654",
      4: "#cc5c5c",
      5: "#ccc",
    };

    return {
      ratingLabels,
      backgroundColors,
    };
  });

  // Actions
  /**
   * Fetches the list of meetings.
   * @param {string|null} teacherShortage - The teacher shortage parameter.
   * @returns {Promise<Array>} The fetched meetings.
   */
  const fetchMeetings = async function (teacherShortage = null) {
    const { employeeId, user } = useHelpers();

    const id = employeeId || user.id;

    if (!id) {
      $q.notify({
        color: "negative",
        message: `Log eerst in als een medewerker om de besprekingen te bekijken`,
      });
    }

    try {
      const { data: fetchedMeetings } = await axios.get(
        `/api/teacher/${id}/ratings`,
        {
          params: {
            ...(teacherShortage && { shortage: teacherShortage }),
          },
        },
      );

      meetings.value = fetchedMeetings;

      fetchedMeetings.forEach((meeting, key) => {
        const { meeting_type } = meeting;
        switch (meeting_type) {
          case "group_meeting":
            meetings.value[key] = new GroupMeeting(meeting);
            break;
          case "transition_meeting":
            meetings.value[key] = new TransitionMeeting(meeting, this);
            break;
          // default:
          //   meetings.value[key] = new Meeting(meeting);
          //   break;
        }
      });

      return meetings.value;
    } catch (error) {
      console.log("🚀 ~ fetchMeetings ~ error:", error);
      if (axios.isAxiosError(error)) {
        return Promise.reject(error);
      }
    }
  };

  const fetchStudentMeetings = async ({ forceFetch = false } = {}) => {
    const { studentId } = useHelpers();

    if (isFetching["studentMeetings"]) {
      return;
    }

    if (!shouldFetch(hasFetched.value["studentMeetings"]) && !forceFetch) {
      return studentMeetings.value || [];
    }

    try {
      isFetching["studentMeetings"] = true;

      const { data } = await axios.get(
        `/api/student/${studentId}/current-subjects`,
      );

      hasFetched.value["studentMeetings"] = new Date();

      studentMeetings.value = data;

      return meetings.value;
    } catch (error) {
      console.log("🚀 ~ fetchStudentMeetings ~ error:", error);

      if (axios.isAxiosError(error)) {
        return Promise.reject(error);
      }
    } finally {
      isFetching["studentMeetings"] = false;
    }
  };

  /**
   * Fetches a meeting by its ID.
   * @param {number} meetingId - The ID of the meeting to fetch.
   * @returns {Promise<Object>} The fetched meeting.
   */
  const fetchStudentMeeting = async function (meetingId) {
    try {
      const { data } = await axios.get(`/api/meetings/${meetingId}`);

      const createMeetingModel = new Meeting(data.data, this);

      meeting.value = createMeetingModel;

      return createMeetingModel;
    } catch (error) {
      console.log("🚀 ~ fetchStudentMeeting ~ error:", error);

      if (axios.isAxiosError(error)) {
        return Promise.reject(error);
      }
    }
  };

  /**
   * Fetches a transition meeting by its ID.
   * @param {number} meetingId - The ID of the meeting to fetch.
   * @returns {Promise<Object>} The fetched meeting.
   */
  const fetchTransitionMeeting = async function (meetingId) {
    try {
      const { schoolId } = useHelpers();

      const { data } = await axios.get(
        `/api/school/${schoolId}/transition_meeting/${meetingId}`,
      );

      const createMeetingModel = new TransitionMeeting(data, this);

      meeting.value = createMeetingModel;

      return createMeetingModel;
    } catch (error) {
      console.log("🚀 ~ fetchTransitionMeeting ~ error:", error);

      if (axios.isAxiosError(error)) {
        return Promise.reject(error);
      }
    }
  };

  const deleteTransitionMeetingStudentData = async (meetingId) => {
    const { schoolId } = useHelpers();

    return await axios.post(
      `/api/school/${schoolId}/transition-meetings/${meetingId}/clear-student-data`,
    );
  };

  /**
   * Fetches a group meeting by its ID.
   * @param {number} meetingId - The ID of the meeting to fetch.
   * @returns {Promise<Object>} The fetched meeting.
   */
  const fetchGroupMeeting = async function (meetingId) {
    const { schoolId } = useHelpers();

    try {
      const { data } = await axios.get(
        `/api/school/${schoolId}/group-meetings/${meetingId}`,
      );

      const createMeetingModel = new GroupMeeting(data, this);

      meeting.value = createMeetingModel;

      initialMeeting.value = cloneDeep(meeting.value);
      return createMeetingModel;
    } catch (error) {
      console.log("🚀 ~ fetchGroupMeeting ~ error:", error);

      if (axios.isAxiosError(error)) {
        return Promise.reject(error);
      }
    }
  };

  /**
   * Fetches the list of teachers.
   * @param {boolean} forceFetch - Whether to force fetch the data.
   * @returns {Promise<Array>} The fetched teachers.
   */
  const fetchTeachers = async (forceFetch = false) => {
    const { schoolId } = useHelpers();

    if (!shouldFetch(hasFetched.value["teachers"]) && !forceFetch) {
      return teachers.value || [];
    }

    try {
      isFetching["teachers"] = true;

      const { data } = await axios.get(
        `/api/school/${schoolId}/group-meetings/teachers`,
      );

      hasFetched.value["teachers"] = new Date();

      teachers.value = data.map(
        ({ teacher_id, teacher_shortage, mentor_classes }) => {
          return {
            teacher_id,
            teacher_shortage,
            mentor_classes: mentor_classes.filter(
              ({ percentage }) => percentage > 0,
            ),
          };
        },
      );

      return teachers.value;
    } catch (error) {
      console.log("🚀 ~ fetchTeachers ~ error:", error);
      if (axios.isAxiosError(error)) {
        return Promise.reject(error);
      }
    } finally {
      isFetching["teachers"] = false;
    }
  };

  /**
   * Removes a meeting.
   * @param {Object} meeting - The meeting to remove.
   * @returns {Promise<void>}
   */
  const removeMeeting = async (meeting) => {
    const { schoolId } = useHelpers();

    try {
      const { meeting_type, id } = meeting;
      let url;

      switch (meeting_type) {
        case "group_meeting":
          url = `/api/school/${schoolId}/group-meetings/${id}`;
          break;
        case "transition_meeting":
          url = `/api/school/${schoolId}/transition_meeting/${id}`;
          break;
        case "meeting":
          url = `/api/meetings/${id}`;
          break;
      }

      return await axios.delete(url);
    } catch (error) {
      console.log("🚀 ~ storeStudentMeeting ~ error:", error);
      if (axios.isAxiosError(error)) {
        console.log("🚀 ~ storeMeeting ~ error:", error);
        return Promise.reject(error);
      }
    }
  };

  /**
   * Updates a meeting.
   * @param {Object} meeting - The meeting to update.
   * @returns {Promise<void>}
   */
  const updateMeeting = async (meeting) => {
    const { schoolId } = useHelpers();

    try {
      const { meeting_type, id } = meeting.value;
      let url;

      switch (meeting_type) {
        case "transition_meeting":
          url = `/api/school/${schoolId}/transition_meeting/${id}/update-global-info`;
          break;
        case "meeting":
          url = `/api/meetings/${id}/update-global-info`;
          break;
      }

      return await axios.post(url, meeting.value);
    } catch (error) {
      console.log("🚀 ~ storeStudentMeeting ~ error:", error);
      if (axios.isAxiosError(error)) {
        console.log("🚀 ~ storeMeeting ~ error:", error);
        return Promise.reject(error);
      }
    }
  };

  /**
   * Updates the selected teachers for a group meeting.
   * @param {Object} meeting - The meeting to update.
   * @returns {Promise<void>}
   */
  const updateGroupMeetingSelectedTeachers = async (meeting) => {
    const { schoolId } = useHelpers();

    try {
      let { teacher_ids } = meeting;
      const { id, teacher_percentage, mentor_classes } = meeting;

      teacher_ids = Object.fromEntries(
        Object.entries(teacher_ids).filter(([_, value]) => value.length),
      );

      await axios.post(
        `api/school/${schoolId}/group-meetings/${id}/update-ratings`,
        { teacher_ids, teacher_percentage, mentor_classes },
      );

      await fetchGroupMeeting(id);
    } catch (error) {
      console.log("🚀 ~ storeStudentMeeting ~ error:", error);
      if (axios.isAxiosError(error)) {
        console.log("🚀 ~ storeMeeting ~ error:", error);
        return Promise.reject(error);
      }
    }
  };

  /**
   * Clears the share feedback date and time.
   */
  const clearShareFeedbackAt = () => {
    if (meeting?.value?.timeframes) {
      meeting.value.timeframes.share_feedback_at_date = null;
      meeting.value.timeframes.share_feedback_at_time = null;
      meeting.value.timeframes.auto_share = 0;
    }

    if (meeting?.value?.timeframe_settings) {
      meeting.value.timeframe_settings.share_feedback_at_date = null;
      meeting.value.timeframe_settings.share_feedback_at_time = null;
      meeting.value.timeframe_settings.auto_share = 0;
    }
  };

  /**
   * Updates the rating types for a group meeting.
   * @param {Object} meeting - The meeting to update.
   * @returns {Promise<void>}
   */
  const updateGroupMeetingRatingTypes = async (meeting) => {
    const { schoolId } = useHelpers();

    try {
      const { id, rating_type_ids } = meeting;

      const { data } = await axios.post(
        `api/school/${schoolId}/group-meetings/${id}/update-rating-types`,
        { rating_type_ids },
      );

      await fetchGroupMeeting(id);

      return data;
    } catch (error) {
      console.log("🚀 ~ updateGroupMeetingRatingTypes ~ error:", error);
      if (axios.isAxiosError(error)) {
        console.log("🚀 ~ storeMeeting ~ error:", error);
        return Promise.reject(error);
      }
    }
  };

  /**
   * Fetches the group meetings for the user's students.
   * @param {boolean} forceFetch - Whether to force fetch the data.
   * @returns {Promise<Array>} The fetched group meetings.
   */
  const fetchMyStudentsGroupMeetings = async (forceFetch = false) => {
    const { employeeId } = useHelpers();

    if (
      !shouldFetch(hasFetched.value["myStudentsGroupMeetings"]) &&
      !forceFetch
    ) {
      return myStudentsGroupMeetings.value || [];
    }

    try {
      const { data } = await axios.get(
        `api/teacher/${employeeId}/mentor-classes/group-meetings`,
      );

      hasFetched.value["myStudentsGroupMeetings"] = new Date();

      data.data.forEach((meeting, key) => {
        myStudentsGroupMeetings.value[key] = new GroupMeeting(meeting);
      });

      return data;
    } catch (error) {
      console.log("🚀 ~ fetchMyStudentsGroupMeetings ~ error:", error);

      if (axios.isAxiosError(error)) {
        return Promise.reject(error);
      }
    }
  };

  /**
   * Fetches the group meetings for the user's lesson groups.
   * @param {boolean} forceFetch - Whether to force fetch the data.
   * @returns {Promise<Array>} The fetched group meetings.
   */
  const fetchMyLessongroupsGroupMeetings = async (forceFetch = false) => {
    const { employeeId } = useHelpers();

    if (
      !shouldFetch(hasFetched.value["myLessongroupsGroupMeetings"]) &&
      !forceFetch
    ) {
      return myLessongroupsGroupMeetings.value || [];
    }

    try {
      const { data } =
        await axios.get(`api/teacher/${employeeId}/lesson-groups/group-meetings
 `);

      hasFetched.value["myLessongroupsGroupMeetings"] = new Date();
      myLessongroupsGroupMeetings.value = data.data;

      return data;
    } catch (error) {
      console.log("🚀 ~ fetchMyLessongroupsGroupMeetings ~ error:", error);

      if (axios.isAxiosError(error)) {
        return Promise.reject(error);
      }
    }
  };

  /**
   * Finds a mentor class in a group meeting.
   * @param {Object} model - The model containing group meetings.
   * @param {number} groupMeetingId - The ID of the group meeting.
   * @param {string} mentorClassTitle - The title of the mentor class.
   * @returns {Object|Error} The found mentor class or an error if not found.
   */
  const findMentorClassInGroupMeeting = (
    model,
    groupMeetingId,
    mentorClassTitle,
  ) => {
    const foundGroupMeeting = model.value.find(
      ({ id }) => id === groupMeetingId,
    );

    if (!foundGroupMeeting) {
      return new Error("Groepsbespreking kan niet gevonden worden");
    }

    const foundMentorClass = foundGroupMeeting.mentor_classes.find(
      ({ title }) => title === mentorClassTitle,
    );

    if (!foundMentorClass) {
      return new Error("Mentorklas kan niet gevonden worden");
    }

    return foundMentorClass;
  };

  const fetchStudentCheckinQuestions = async ({
    forceFetch = false,
    meetingId,
  } = {}) => {
    const { schoolId } = useHelpers();

    if (isFetching["studentCheckinQuestions"]) {
      return;
    }

    if (
      !shouldFetch(hasFetched.value["studentCheckinQuestions"]) &&
      !forceFetch
    ) {
      return studentCheckinQuestions.value || [];
    }

    try {
      isFetching["studentCheckinQuestions"] = true;

      const { data: choices } = await axios.get(
        `api/school/${schoolId}/student-checkin-questions`,
      );

      hasFetched.value["studentCheckinQuestions"] = new Date();

      studentCheckinQuestions.value = choices.map(
        ({ question, id, rating_field }) => {
          return {
            meeting_id: meetingId,
            question_id: id,
            rating_field,
            question,
            question_rating: null,
            question_answer: null,
          };
        },
      );

      return studentCheckinQuestions.value;
    } catch (error) {
      console.log("🚀 ~ fetchStudentCheckinQuestions ~ error:", error);

      if (axios.isAxiosError(error)) {
        return Promise.reject(error);
      }
    } finally {
      isFetching["studentCheckinQuestions"] = false;
    }
  };

  const fetchMeetingTimelineTemplates = async ({ force = false } = {}) => {
    if (isFetching["meetingTimelineTemplates"]) {
      return isFetching["meetingTimelineTemplates"];
    }

    if (!shouldFetch(hasFetched.value["meetingTimelineTemplates"]) && !force) {
      return meetingTimelineTemplates.value || [];
    }

    isFetching["meetingTimelineTemplates"] = (async () => {
      try {
        const {
          data: { data: timelineTemplates, meta },
        } = await axios.get("api/transition_meeting/timeline_options");

        // Set the time of the last fetch
        hasFetched.value["meetingTimelineTemplates"] = new Date();

        // Reset the current value
        meetingTimelineOptions.value = [];
        meetingTimelineTemplates.value = [];

        // Add the meta options
        for (const [key, value] of Object.entries(meta)) {
          meetingTimelineOptions.value.push({
            model: key,
            value,
          });
        }

        // Add the fetched templates to the current value
        timelineTemplates.forEach((template) =>
          meetingTimelineTemplates.value.push(template),
        );

        return meetingTimelineTemplates.value;
      } catch (error) {
        console.log(
          "🚀 ~ isFetching[meetingTimelineTemplates]= ~ error:",
          error,
        );
      } finally {
        isFetching["meetingTimelineTemplates"] = false;
      }
    })();

    return isFetching["meetingTimelineTemplates"];
  };

  const shareAllFeedback = async ({
    meetingId = null,
    meetingType = "meeting",
  } = {}) => {
    if (!meetingId) {
      return Promise.reject("Missing meetingId");
    }

    switch (meetingType) {
      case "meeting": {
        let formData = new FormData();
        formData.set("meeting_id", meetingId);
        return await axios.post(`/api/share-feedback-bulk`, formData);
      }
      case "transition_meeting":
        return await axios.post(
          `/api/transition-meetings/${meetingId}/share-all-feedback`,
        );
      default:
        return Promise.reject("Invalid meeting type");
    }
  };

  return {
    meeting,
    studentMeetings,
    initialMeeting,
    isFetching,
    teachers,
    meetings,
    timelines,
    meetingTimelineTemplates,
    meetingTimelineOptions,
    myStudentsGroupMeetings,
    myLessongroupsGroupMeetings,
    studentCheckinQuestions,
    fetchMeetings,
    fetchStudentMeetings,
    fetchTeachers,
    fetchMeetingTimeline,
    initialStudents,
    initialSchedules,
    isAllowed,
    isOverview,
    isEditable,
    groupMeetingConfig,
    fetchStudentMeeting,
    fetchTransitionMeeting,
    fetchGroupMeeting,
    removeMeeting,
    updateMeeting,
    updateGroupMeetingSelectedTeachers,
    clearShareFeedbackAt,
    updateGroupMeetingRatingTypes,
    fetchMyStudentsGroupMeetings,
    fetchMyLessongroupsGroupMeetings,
    fetchStudentCheckinQuestions,
    fetchMeetingTimelineTemplates,
    shareAllFeedback,
    deleteTransitionMeetingStudentData,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useMeetingStore, import.meta.hot));
}
